import React, { useEffect, useRef, useState } from 'react';

const STYLE_ID = 'wingr-mascot-styles';
const BODY = '/wingr-mascot-body.png?v=9';
const FACE = '/wingr-mascot-face.png?v=9';
const EYES = '/wingr-mascot-eyes.png?v=3';
const ASPECT = 315 / 235;
const GLANCES = [
  [0, 0],
  [-1, 0],
  [0, 0],
  [1, 0],
  [0, 0],
  [0, -1],
  [0, 0],
  [0, 1],
];

const CSS = `
.wgm{position:relative;display:inline-block;line-height:0;vertical-align:middle;overflow:visible;flex-shrink:0}
.wgm-rig{display:block;width:100%;height:100%;transform-origin:50% 62%}
.wgm-look{display:block;width:100%;height:100%;position:relative;transition:transform .28s cubic-bezier(.2,.8,.2,1)}
.wgm-art{width:100%;height:100%;display:block;object-fit:contain;pointer-events:none;user-select:none}
.wgm-face,.wgm-eyes{position:absolute;inset:0;transition:transform .28s cubic-bezier(.2,.8,.2,1)}
.wgm-pupils{position:absolute;inset:0;transform-origin:76% 51%;transition:transform .14s ease}
.wgm.squint .wgm-pupils{transform:scaleY(.16)}
.wgm-lid{position:absolute;border-radius:999px;background:#071833;transform-origin:center top;transform:scaleY(0);pointer-events:none}
.wgm.blinking .wgm-lid{animation:wgm-blink .16s ease-in-out}
.wgl{display:inline-flex;align-items:center;gap:.42em;white-space:nowrap;line-height:1;text-decoration:none}
.wgl-word{font-weight:800;letter-spacing:.14em;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;color:#fff}
html:not(.dark) .wgl-word{color:#0f172a}
@keyframes wgm-blink{0%{transform:scaleY(0)}40%{transform:scaleY(1)}100%{transform:scaleY(0)}}
@keyframes wgm-bounce{0%,100%{transform:translateY(0) scale(1)}40%{transform:translateY(-12%) scale(1.04)}70%{transform:translateY(2%) scale(.98)}}
@keyframes wgm-happy{0%{transform:translateY(0) scale(1)}35%{transform:translateY(-16%) scale(1.05) rotate(-4deg)}65%{transform:translateY(0) scale(1) rotate(3deg)}100%{transform:translateY(0) scale(1) rotate(0)}}
@keyframes wgm-pulse{0%,100%{filter:drop-shadow(0 0 0 rgba(56,189,248,0))}50%{filter:drop-shadow(0 0 14px rgba(56,189,248,.95))}}
.wgm.mood-greeting .wgm-rig{animation:wgm-bounce .7s ease-in-out 2}
.wgm.mood-success .wgm-rig{animation:wgm-happy .6s cubic-bezier(.2,1.3,.4,1) 2}
.wgm.mood-thinking .wgm-body{animation:wgm-pulse 1.05s ease-in-out infinite}
`;

function ensureStyles() {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = CSS;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Wingr mascot. mood: idle | listening | thinking | success | greeting
 * The face and eyes slide left, right, up, down, and back to centre (straight ahead).
 */
export function WingrMascot({ size = 64, mood = 'idle', className = '' }) {
  const ref = useRef(null);
  const mouseAt = useRef(0);
  const [blink, setBlink] = useState(false);
  const [squint, setSquint] = useState(false);
  const [look, setLook] = useState({ x: 0, y: 0 });
  const [gaze, setGaze] = useState({ x: 0, y: 0 });
  const blinkOn = useRef(false);
  const squintOn = useRef(false);
  const stanceRef = useRef({ mode: 'free', x: 0, y: 0 });

  useEffect(() => {
    ensureStyles();
  }, []);

  useEffect(() => {
    let alive = true;
    let timer;
    const schedule = () => {
      const wait = 2400 + Math.random() * 2600;
      timer = setTimeout(() => {
        if (!alive) return;
        if (squintOn.current) {
          schedule();
          return;
        }
        blinkOn.current = true;
        setBlink(true);
        setTimeout(() => {
          blinkOn.current = false;
          if (alive) setBlink(false);
        }, 170);
        schedule();
      }, wait);
    };
    schedule();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let timer;
    const schedule = () => {
      const wait = 4200 + Math.random() * 3800;
      timer = setTimeout(() => {
        if (!alive) return;
        if (blinkOn.current) {
          schedule();
          return;
        }
        squintOn.current = true;
        setSquint(true);
        setTimeout(() => {
          squintOn.current = false;
          if (alive) setSquint(false);
        }, 620);
        schedule();
      }, wait);
    };
    schedule();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let think = 0;
    let stanceStep = 0;
    let pose = 0;
    const aim = (dx, dy) => {
      const dead = 0.2;
      const axis = (v) => (Math.abs(v) < dead ? 0 : clamp(v / 0.85, -1, 1));
      return { x: axis(dx), y: axis(dy) };
    };
    const eyePose = (pointer) => {
      const stance = stanceRef.current;
      if (stance.mode !== 'down') return pointer;
      return {
        x: clamp(stance.x + pointer.x * 0.5, -3.8, 3.8),
        y: clamp(stance.y + pointer.y * 0.5, -4, 4),
      };
    };
    const onMove = (event) => {
      if (mood !== 'idle') return;
      const rect = el.getBoundingClientRect();
      const dx = (event.clientX - (rect.left + rect.width / 2)) / Math.max(rect.width, 1);
      const dy = (event.clientY - (rect.top + rect.height / 2)) / Math.max(rect.height, 1);
      mouseAt.current = Date.now();
      const pointer = aim(dx, dy);
      setLook(pointer);
      setGaze(eyePose(pointer));
    };
    const tick = setInterval(() => {
      if (mood === 'listening' || mood === 'success' || mood === 'greeting') {
        const poseNow = { x: 0, y: mood === 'listening' ? -0.12 : 0 };
        setLook(poseNow);
        setGaze(poseNow);
        return;
      }
      if (mood === 'thinking') {
        think += 0.55;
        const poseNow = { x: Math.sin(think), y: Math.sin(think * 0.45) * 0.35 };
        setLook(poseNow);
        setGaze(poseNow);
        return;
      }
      if (Date.now() - mouseAt.current < 1400) return;
      setLook({ x: 0, y: 0 });
      const stance = stanceRef.current;
      if (stance.mode === 'down') {
        setGaze({ x: stance.x, y: stance.y });
        return;
      }
      const next = GLANCES[pose % GLANCES.length];
      pose += 1;
      setGaze({ x: next[0], y: next[1] });
    }, 850);
    let stanceTimer;
    const scheduleStance = () => {
      const wait = stanceStep === 0 ? 4800 : 2200;
      stanceTimer = setTimeout(() => {
        if (mood === 'idle') {
          stanceStep = (stanceStep + 1) % 3;
          if (stanceStep === 1) stanceRef.current = { mode: 'down', x: -3.3, y: 3.5 };
          else if (stanceStep === 2) stanceRef.current = { mode: 'down', x: 3.3, y: 3.5 };
          else stanceRef.current = { mode: 'free', x: 0, y: 0 };
          if (Date.now() - mouseAt.current >= 1400) {
            setLook({ x: 0, y: 0 });
            setGaze({ x: stanceRef.current.x, y: stanceRef.current.y });
          }
        }
        scheduleStance();
      }, wait);
    };
    scheduleStance();
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearInterval(tick);
      clearTimeout(stanceTimer);
    };
  }, [mood]);

  const height = size;
  const width = Math.round(size * ASPECT);

  return (
    <span
      ref={ref}
      className={`wgm mood-${mood} ${blink ? 'blinking' : ''} ${squint ? 'squint' : ''} ${className}`.trim()}
      style={{ width, height }}
      role="img"
      aria-label="Wingr mascot"
    >
      <span className="wgm-rig">
        <span className="wgm-look" style={{ transform: `translate(${look.x * 0.55}%, ${look.y * 0.6}%)` }}>
          <img className="wgm-art wgm-body" src={BODY} alt="" draggable="false" />
          <span className="wgm-face" style={{ transform: `translate(${look.x * 0.7}%, ${look.y * 0.65}%)` }}>
            <img className="wgm-art" src={FACE} alt="" draggable="false" />
            <span className="wgm-eyes" style={{ transform: `translate(${gaze.x * 1.35}%, ${gaze.y * 1.45}%)` }}>
              <span className="wgm-pupils">
                <img className="wgm-art" src={EYES} alt="" draggable="false" />
              </span>
              <span className="wgm-lid" style={{ left: '62.2%', top: '37%', width: '10.5%', height: '24%' }} />
              <span className="wgm-lid" style={{ left: '80.6%', top: '40.9%', width: '9.6%', height: '23.4%' }} />
            </span>
          </span>
        </span>
      </span>
    </span>
  );
}

/**
 * Wordmark: WINGR {mascot} BOT
 * mood "auto" plays a greeting, then idles. Hover leans in to listen.
 */
export function WingrLockup({ size = 40, mood = 'auto', className = '' }) {
  const [live, setLive] = useState(mood === 'auto' ? 'greeting' : mood);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (mood !== 'auto') {
      setLive(mood);
      return undefined;
    }
    setLive('greeting');
    const timer = setTimeout(() => setLive('idle'), 1500);
    return () => clearTimeout(timer);
  }, [mood]);

  const shown = hover && (live === 'idle' || live === 'greeting') ? 'listening' : live;
  const word = Math.max(11, Math.round(size * 0.42));

  return (
    <span
      className={`wgl ${className}`.trim()}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span className="wgl-word" style={{ fontSize: word }}>WINGR</span>
      <WingrMascot size={size} mood={shown} />
      <span className="wgl-word" style={{ fontSize: word }}>BOT</span>
    </span>
  );
}

export default WingrLockup;
